import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogIn, UserPlus, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/dcjy7jep_MEDX%20AI%20Partnership%20Proposal.png";

export function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const navItems = [
        { to: "/", label: t("nav.home"), key: "home" },
        { to: "/about", label: t("nav.about"), key: "about" },
        { to: "/solutions", label: t("nav.solutions"), key: "solutions" },
        { to: "/industries", label: t("nav.industries"), key: "industries" },
        { to: "/our-reach", label: t("nav.ourReach"), key: "our-reach" },
        { to: "/mobile-app", label: t("nav.mobileApp"), key: "mobile-app" },
        { to: "/contact", label: t("nav.contact"), key: "contact" },
    ];
    const desktopNavItems = navItems.filter((i) => i.to !== "/contact");

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/85 border-b border-gray-100"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
                <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
                    <img src={LOGO} alt="Medx AI — Medical AI · Innovation · Technology" className="h-10 md:h-12 w-auto" />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {desktopNavItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `px-3 py-2 rounded-full text-sm font-medium transition-all ${
                                    isActive
                                        ? "bg-red-50 text-[var(--medx-red)]"
                                        : "text-gray-700 hover:bg-orange-50 hover:text-[var(--medx-orange)]"
                                }`
                            }
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    {user ? (
                        <>
                            <button
                                onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                                className="btn-outline !py-2 !px-4 text-sm"
                                data-testid="nav-dashboard-btn"
                            >
                                {t("nav.dashboard")}
                            </button>
                            <button
                                onClick={() => { logout(); navigate("/"); }}
                                className="text-sm text-gray-600 hover:text-[var(--medx-red)] px-3 py-2 rounded-full"
                                data-testid="nav-logout-btn"
                            >
                                {t("nav.signout")}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="btn-outline !py-2 !px-4 text-sm flex items-center gap-2" data-testid="nav-login-btn">
                                <LogIn className="w-4 h-4" /> {t("nav.login")}
                            </Link>
                            <Link to="/signup" className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2" data-testid="nav-signup-btn">
                                <UserPlus className="w-4 h-4" /> {t("nav.signup")}
                            </Link>
                            <Link to="/order" className="bg-gray-900 hover:bg-black text-white !py-2 !px-4 text-sm rounded-full font-semibold flex items-center gap-2 transition-all shadow-[0_8px_24px_-8px_rgba(0,0,0,0.45)]" data-testid="nav-order-btn">
                                <ShoppingCart className="w-4 h-4" /> {t("nav.order")}
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="navbar-mobile-toggle"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-gray-100 bg-white" data-testid="navbar-mobile-menu">
                    <div className="px-5 py-4 flex flex-col gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `px-3 py-2.5 rounded-lg text-sm font-medium ${
                                        isActive ? "bg-red-50 text-[var(--medx-red)]" : "text-gray-700 hover:bg-gray-50"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <div className="border-t border-gray-100 mt-2 pt-3 flex flex-col gap-2">
                            {user ? (
                                <>
                                    <button onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/dashboard"); }} className="btn-outline text-sm">{t("nav.dashboard")}</button>
                                    <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-sm text-gray-600 px-3 py-2">{t("nav.signout")}</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" onClick={() => setOpen(false)} className="btn-outline text-sm text-center">{t("nav.login")}</Link>
                                    <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary text-sm text-center">{t("nav.signup")}</Link>
                                    <Link to="/doctor/login" onClick={() => setOpen(false)} className="text-xs text-center text-gray-600 py-2">{t("nav.doctorPortal")} →</Link>
                                    <Link to="/employer/login" onClick={() => setOpen(false)} className="text-xs text-center text-gray-600 py-2" data-testid="nav-mobile-employer-link">{t("nav.employerPortal")} →</Link>
                                    <Link to="/order" onClick={() => setOpen(false)} className="bg-gray-900 text-white rounded-full py-2.5 text-sm text-center font-semibold">{t("nav.order")}</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
